import { useCallback, useEffect, useRef, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService, adminStorage } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Spinner from "../../components/ui/Spinner";
import { useAdminToast } from "../../context/AdminToastContext";

type PromoBannerType = "category" | "seller_deal" | "direct_link";

const PROMO_BANNER_TYPE_OPTIONS: { value: PromoBannerType; label: string; hint: string }[] = [
  { value: "category", label: "Category only", hint: "Clicking the banner routes to the category feed only." },
  { value: "seller_deal", label: "Seller / Partner deal", hint: "Clicking the banner opens a specific deal from a chosen seller or partner." },
  { value: "direct_link", label: "Direct link", hint: "Clicking the banner opens an admin-provided URL (internal or external)." },
];

interface SellerOpt {
  id: string;
  name: string;
  email?: string;
  role?: string;
}

interface DealOpt {
  id: string;
  collection: string;
  title: string;
  category_slug?: string;
}

interface Banner {
  id: string;
  title: string | null;
  category_slug: string;
  image_url: string | null;
  video_url: string | null;
  active: boolean;
  priority: number;
  banner_type?: PromoBannerType;
  seller_id?: string | null;
  seller_name?: string | null;
  seller_role?: string | null;
  deal_id?: string | null;
  deal_collection?: string | null;
  deal_title?: string | null;
  direct_link?: string | null;
  scheduled_deactivate_at?: string | null;
  effective_status?: "active" | "expired" | "inactive";
  created_at?: string;
  updated_at?: string;
}

const emptyBannerForm = () => ({
  title: "",
  category_slug: "",
  active: true,
  priority: 0,
  banner_type: "category" as PromoBannerType,
  direct_link: "",
  // Auto-deactivation. `deactivate_at` is a <input type="datetime-local">
  // value (local time, no zone); the duration fields are a convenience that
  // recomputes it as now + duration. Empty = never expires.
  deactivate_at: "",
  deactivate_days: "",
  deactivate_hours: "",
  deactivate_minutes: "",
  deactivate_seconds: "",
});

/** Format a Date as the "YYYY-MM-DDTHH:mm" value <input type="datetime-local"> expects. */
function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminPromoBannersPage() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);

  // ── Category slugs for banner autocomplete ────────────────────────────────────
  // Uses the admin listing (all categories, including ones with zero live deals)
  // rather than /api/v2/categories?view=summary, which drops any category/
  // subcategory with a current deal count of 0 — that summary endpoint is meant
  // for public browse surfaces, and using it here silently hid valid slugs from
  // this picker.
  const [categorySlugs, setCategorySlugs] = useState<string[]>([]);
  useEffect(() => {
    apiClient.get<Array<{ slug: string }>>("/api/admin/v2/categories", { headers: adminHeaders() })
      .then((res) => {
        const slugs = new Set<string>();
        for (const cat of res.data ?? []) {
          if (cat.slug) slugs.add(cat.slug);
        }
        setCategorySlugs([...slugs].sort());
      })
      .catch(() => {});
  }, []);

  // ── Banners ───────────────────────────────────────────────────────────────────
  const [banners, setBanners] = useState<Banner[]>([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [bannerSaving, setBannerSaving] = useState<string | null>(null);
  const [editingBannerId, setEditingBannerId] = useState<string | null>(null);
  const [bannerForm, setBannerForm] = useState(emptyBannerForm());
  const [bannerImageFile, setBannerImageFile] = useState<File | null>(null);
  const [bannerImagePreview, setBannerImagePreview] = useState<string | null>(null);
  const [bannerVideoFile, setBannerVideoFile] = useState<File | null>(null);
  const [bannerVideoPreview, setBannerVideoPreview] = useState<string | null>(null);
  const [deleteBannerTarget, setDeleteBannerTarget] = useState<Banner | null>(null);
  const [viewBanner, setViewBanner] = useState<Banner | null>(null);
  const bannerFileRef = useRef<HTMLInputElement>(null);
  const bannerVideoRef = useRef<HTMLInputElement>(null);

  // Seller/deal picker state (banner_type === "seller_deal")
  const [sellerRoleFilter, setSellerRoleFilter] = useState<string>("");
  const [sellerSearch, setSellerSearch] = useState("");
  const [sellerOptions, setSellerOptions] = useState<SellerOpt[]>([]);
  const [sellerDropdownOpen, setSellerDropdownOpen] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<SellerOpt | null>(null);

  const [dealSearch, setDealSearch] = useState("");
  const [dealOptions, setDealOptions] = useState<DealOpt[]>([]);
  const [dealDropdownOpen, setDealDropdownOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<DealOpt | null>(null);

  useEffect(() => {
    if (!sellerDropdownOpen) return;
    const t = setTimeout(() => {
      adminService
        .searchHomeDealBannerSellers({ search: sellerSearch || undefined, role: sellerRoleFilter || undefined })
        .then(setSellerOptions)
        .catch(() => setSellerOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [sellerSearch, sellerRoleFilter, sellerDropdownOpen]);

  useEffect(() => {
    if (!dealDropdownOpen || !selectedSeller) return;
    const t = setTimeout(() => {
      adminService
        .searchHomeDealBannerSellerDeals(selectedSeller.id, { search: dealSearch || undefined })
        .then(setDealOptions)
        .catch(() => setDealOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [dealSearch, dealDropdownOpen, selectedSeller]);

  const loadBanners = useCallback(async () => {
    setBannersLoading(true);
    try {
      const res = await apiClient.get<Banner[]>("/rag/banners/admin", { headers: adminHeaders() });
      setBanners(res.data);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setBannersLoading(false);
    }
  }, []);

  useEffect(() => { void loadBanners(); }, [loadBanners]);

  const handleBannerImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setBannerImageFile(file);
    setBannerImagePreview(file ? URL.createObjectURL(file) : null);
  };

  const handleBannerVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setBannerVideoFile(file);
    setBannerVideoPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleBannerEdit = (banner: Banner) => {
    setEditingBannerId(banner.id);
    setBannerForm({
      title: banner.title ?? "",
      category_slug: banner.category_slug,
      active: banner.active,
      priority: banner.priority,
      banner_type: banner.banner_type || "category",
      direct_link: banner.direct_link || "",
      deactivate_at: banner.scheduled_deactivate_at
        ? toLocalInputValue(new Date(banner.scheduled_deactivate_at))
        : "",
      deactivate_days: "",
      deactivate_hours: "",
      deactivate_minutes: "",
      deactivate_seconds: "",
    });
    setBannerImageFile(null);
    setBannerImagePreview(banner.image_url);
    setBannerVideoFile(null);
    setBannerVideoPreview(banner.video_url);
    setSelectedSeller(
      banner.seller_id
        ? { id: banner.seller_id, name: banner.seller_name || banner.seller_id, role: banner.seller_role || undefined }
        : null,
    );
    setSelectedDeal(
      banner.deal_id
        ? { id: banner.deal_id, collection: banner.deal_collection || "", title: banner.deal_title || banner.deal_id }
        : null,
    );
    setSellerSearch("");
    setDealSearch("");
    setSellerRoleFilter("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBannerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (bannerForm.banner_type === "category" && !bannerForm.category_slug.trim()) {
      // Only "Category only" banners route exclusively via category_slug — for
      // "Seller / Partner deal" it's optional (routes via seller_id/deal_id,
      // falling back to the generic deals feed) and "Direct link" doesn't use
      // it at all. Scroll the error into view — without this the admin sees
      // nothing happen, since the message renders in the shared banner at the
      // top of the page while they're scrolled down at the form/Update button.
      setError("Category slug is required for this banner type. Please fill in the Category Slug field above.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // The backend rejects a past auto-deactivation time with a 400; catch it
    // here first so the admin gets the message next to the field they typed in.
    let deactivateIso = "";
    if (bannerForm.deactivate_at) {
      const when = new Date(bannerForm.deactivate_at);
      if (Number.isNaN(when.getTime())) {
        setError("Auto-deactivate date-time is not a valid date.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (when.getTime() <= Date.now()) {
        setError("Auto-deactivate date-time must be in the future.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      deactivateIso = when.toISOString();
    }
    setBannerSaving("form"); setError(null);
    try {
      const fd = new FormData();
      fd.append("category_slug", bannerForm.category_slug.trim());
      // Always send title/seller/deal/direct_link — even empty — so clearing a
      // field in the edit form actually clears it on save instead of the
      // previous value silently surviving because the key was omitted.
      fd.append("title", bannerForm.title);
      fd.append("active", String(bannerForm.active));
      fd.append("priority", String(bannerForm.priority));
      fd.append("banner_type", bannerForm.banner_type);
      fd.append("seller_id", selectedSeller?.id || "");
      fd.append("seller_name", selectedSeller?.name || "");
      fd.append("seller_role", selectedSeller?.role || "");
      fd.append("deal_id", selectedSeller?.role === "partner" ? "" : selectedDeal?.id || "");
      fd.append("direct_link", bannerForm.direct_link);
      // Always sent — an empty value clears an existing schedule on edit.
      fd.append("scheduled_deactivate_at", deactivateIso);
      if (bannerImageFile) fd.append("image", bannerImageFile);
      if (bannerVideoFile) fd.append("video", bannerVideoFile);

      if (editingBannerId) {
        await apiClient.put(`/rag/banners/${editingBannerId}`, fd, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } });
        setSuccess("Banner updated.");
      } else {
        await apiClient.post("/rag/banners", fd, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } });
        setSuccess("Banner created.");
      }
      setBannerForm(emptyBannerForm());
      setEditingBannerId(null);
      setBannerImageFile(null);
      setBannerImagePreview(null);
      setBannerVideoFile(null);
      setBannerVideoPreview(null);
      setSelectedSeller(null);
      setSelectedDeal(null);
      setSellerSearch("");
      setDealSearch("");
      setSellerRoleFilter("");
      if (bannerFileRef.current) bannerFileRef.current.value = "";
      if (bannerVideoRef.current) bannerVideoRef.current.value = "";
      await loadBanners();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setBannerSaving(null);
    }
  };

  const handleBannerDelete = async () => {
    if (!deleteBannerTarget) return;
    setBannerSaving(deleteBannerTarget.id);
    try {
      await apiClient.delete(`/rag/banners/${deleteBannerTarget.id}`, { headers: adminHeaders() });
      setDeleteBannerTarget(null);
      setSuccess("Banner deleted.");
      await loadBanners();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setBannerSaving(null);
    }
  };

  const handleBannerToggleActive = async (banner: Banner) => {
    setBannerSaving(banner.id + "active");
    try {
      const fd = new FormData();
      fd.append("active", String(!banner.active));
      await apiClient.put(`/rag/banners/${banner.id}`, fd, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } });
      await loadBanners();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setBannerSaving(null);
    }
  };

  return (
    <>
    <div>
      <AdminPageHeader
        title="Promotional Banners"
        subtitle="Upload banners shown in the chat welcome screen"
        actions={
          <button onClick={() => void loadBanners()} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm text-gray-300 transition-colors">
            Refresh
          </button>
        }
      />

      {error && <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>}
      {success && <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>}

      {/* ── Banners ─────────────────────────────────────────────────────────── */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
        <h2 className="text-sm font-semibold text-white mb-1">Promotional Banners</h2>
        <p className="text-gray-500 text-xs mb-4">
          Upload banners shown in the chat welcome screen. Choose a banner type below to control what clicking it does.
        </p>

        {/* Banner create/edit form */}
        <form onSubmit={(e) => void handleBannerSubmit(e)} className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Category Slug {bannerForm.banner_type === "direct_link" ? "(optional)" : "*"}
              </label>
              <input
                list="category-slug-options"
                value={bannerForm.category_slug}
                onChange={(e) => setBannerForm((f) => ({ ...f, category_slug: e.target.value }))}
                placeholder="e.g. beach-holidays"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
              <datalist id="category-slug-options">
                {categorySlugs.map((s) => <option key={s} value={s} />)}
              </datalist>
              <p className="text-gray-600 text-xs mt-1">Category this banner is tagged with (routes here when banner type is "Category only")</p>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Title (optional)</label>
              <input
                value={bannerForm.title}
                onChange={(e) => setBannerForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Explore Beach Holidays"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Banner type</label>
            <div className="flex flex-wrap gap-4">
              {PROMO_BANNER_TYPE_OPTIONS.map((o) => (
                <label key={o.value} className="flex items-center gap-2 text-sm text-gray-300" title={o.hint}>
                  <input
                    type="radio"
                    name="promo_banner_type"
                    checked={bannerForm.banner_type === o.value}
                    onChange={() => setBannerForm((f) => ({ ...f, banner_type: o.value }))}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          {bannerForm.banner_type === "direct_link" && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Direct link</label>
              <input
                value={bannerForm.direct_link}
                onChange={(e) => setBannerForm((f) => ({ ...f, direct_link: e.target.value }))}
                placeholder="https://… or /deals/category/…"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
            </div>
          )}

          {bannerForm.banner_type === "seller_deal" && (
            <div className="border border-gray-800 rounded-xl p-4 space-y-3">
              <label className="block text-xs text-gray-400">Seller / Partner deal</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <select
                    value={sellerRoleFilter}
                    onChange={(e) => setSellerRoleFilter(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-white mb-1"
                  >
                    <option value="">All roles</option>
                    <option value="seller">Seller</option>
                    <option value="partner">Partner</option>
                  </select>
                  <div className="relative">
                    <input
                      value={selectedSeller ? selectedSeller.name : sellerSearch}
                      onChange={(e) => { setSellerSearch(e.target.value); setSelectedSeller(null); setSelectedDeal(null); }}
                      onFocus={() => setSellerDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setSellerDropdownOpen(false), 150)}
                      placeholder="Search seller/partner…"
                      className={`w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white ${selectedSeller ? "pr-8" : ""}`}
                    />
                    {selectedSeller && (
                      <button
                        type="button"
                        onClick={() => { setSelectedSeller(null); setSelectedDeal(null); setSellerSearch(""); }}
                        title="Remove assigned seller/partner"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-400 text-sm leading-none"
                      >
                        ✕
                      </button>
                    )}
                    {sellerDropdownOpen && sellerOptions.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg max-h-56 overflow-y-auto">
                        {sellerOptions.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            onMouseDown={() => { setSelectedSeller(s); setSellerSearch(""); setSellerDropdownOpen(false); setSelectedDeal(null); setDealSearch(""); }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                          >
                            <div>{s.name} <span className="text-gray-500 text-xs">({s.role})</span></div>
                            {s.email && <div className="text-[11px] text-gray-500">{s.email}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {selectedSeller?.role === "partner" ? (
                  <div>
                    <div className="text-[11px] text-gray-500 mb-1 h-[22px] flex items-center">Deal</div>
                    <div className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-500">
                      Partners don't have individual deals — this banner links to their partner page.
                    </div>
                  </div>
                ) : (
                <div>
                  <div className="text-[11px] text-gray-500 mb-1 h-[22px] flex items-center">
                    {selectedSeller ? "Deals for selected seller" : "Select a seller first"}
                  </div>
                  <div className="relative">
                    <input
                      value={selectedDeal ? selectedDeal.title : dealSearch}
                      onChange={(e) => { setDealSearch(e.target.value); setSelectedDeal(null); }}
                      onFocus={() => setDealDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setDealDropdownOpen(false), 150)}
                      disabled={!selectedSeller}
                      placeholder="Search deal…"
                      className={`w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50 ${selectedDeal ? "pr-8" : ""}`}
                    />
                    {selectedDeal && (
                      <button
                        type="button"
                        onClick={() => { setSelectedDeal(null); setDealSearch(""); }}
                        title="Remove assigned deal"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-400 text-sm leading-none"
                      >
                        ✕
                      </button>
                    )}
                    {dealDropdownOpen && dealOptions.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg max-h-56 overflow-y-auto">
                        {dealOptions.map((d) => (
                          <button
                            key={`${d.collection}:${d.id}`}
                            type="button"
                            onMouseDown={() => { setSelectedDeal(d); setDealSearch(""); setDealDropdownOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                          >
                            {d.title} <span className="text-gray-500 text-xs">({d.collection})</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-gray-700 bg-gray-800/40 p-3">
            <div className="flex items-baseline justify-between mb-2">
              <label className="text-xs font-semibold text-gray-300">Auto-deactivate <span className="font-normal text-gray-600">(optional — leave blank to stay active until switched off)</span></label>
              {bannerForm.deactivate_at && (
                <button type="button" onClick={() => setBannerForm((f) => ({ ...f, deactivate_at: "", deactivate_days: "", deactivate_hours: "", deactivate_minutes: "", deactivate_seconds: "" }))}
                  className="text-xs text-gray-400 hover:text-white underline">Clear</button>
              )}
            </div>
            <div className="grid grid-cols-4 md:grid-cols-5 gap-2 items-end">
              {([
                ["deactivate_days", "Days"],
                ["deactivate_hours", "Hours"],
                ["deactivate_minutes", "Minutes"],
                ["deactivate_seconds", "Seconds"],
              ] as const).map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs text-gray-400 mb-1">{label}</label>
                  <input
                    type="number" min={0} placeholder="0"
                    value={bannerForm[key]}
                    onChange={(e) => setBannerForm((f) => {
                      const next = { ...f, [key]: e.target.value };
                      // Changing any duration input recomputes the date-time as
                      // now + duration; the field itself stays directly editable.
                      const secs =
                        (Number(next.deactivate_days) || 0) * 86400 +
                        (Number(next.deactivate_hours) || 0) * 3600 +
                        (Number(next.deactivate_minutes) || 0) * 60 +
                        (Number(next.deactivate_seconds) || 0);
                      next.deactivate_at = secs > 0 ? toLocalInputValue(new Date(Date.now() + secs * 1000)) : "";
                      return next;
                    })}
                    className="w-full px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                </div>
              ))}
              <div className="col-span-4 md:col-span-1">
                <label className="block text-xs text-gray-400 mb-1">Deactivates at</label>
                <input
                  type="datetime-local"
                  min={toLocalInputValue(new Date())}
                  value={bannerForm.deactivate_at}
                  onChange={(e) => setBannerForm((f) => ({ ...f, deactivate_at: e.target.value }))}
                  className="w-full px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Priority</label>
              <input
                type="number"
                value={bannerForm.priority}
                onChange={(e) => setBannerForm((f) => ({ ...f, priority: Number(e.target.value) }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input type="checkbox" checked={bannerForm.active} onChange={(e) => setBannerForm((f) => ({ ...f, active: e.target.checked }))}
                  className="w-4 h-4 rounded accent-indigo-500" />
                Active
              </label>
            </div>
            <div className="md:col-span-2 space-y-2">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Banner Image</label>
                <input ref={bannerFileRef} type="file" accept="image/*" onChange={handleBannerImageChange}
                  className="w-full text-xs text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-gray-700 file:text-gray-300 file:text-xs cursor-pointer" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Banner Video <span className="text-gray-600">(optional, replaces image)</span></label>
                <input ref={bannerVideoRef} type="file" accept="video/*" onChange={handleBannerVideoChange}
                  className="w-full text-xs text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-gray-700 file:text-gray-300 file:text-xs cursor-pointer" />
              </div>
            </div>
          </div>

          {bannerImagePreview && !bannerVideoFile && (
            <div className="relative w-full max-w-sm rounded-xl overflow-hidden border border-gray-700">
              <img src={bannerImagePreview} alt="preview" className="w-full object-cover max-h-32" />
              <button type="button" onClick={() => { setBannerImageFile(null); setBannerImagePreview(null); if (bannerFileRef.current) bannerFileRef.current.value = ""; }}
                className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-lg hover:bg-black/80">
                ✕
              </button>
            </div>
          )}

          {bannerVideoPreview && (
            <div className="relative w-full max-w-sm rounded-xl overflow-hidden border border-gray-700">
              <video src={bannerVideoPreview} className="w-full max-h-32 object-cover" muted loop autoPlay playsInline />
              <button type="button" onClick={() => { setBannerVideoFile(null); setBannerVideoPreview(null); if (bannerVideoRef.current) bannerVideoRef.current.value = ""; }}
                className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-lg hover:bg-black/80">
                ✕
              </button>
            </div>
          )}

          <div className="flex gap-3">
            <button type="submit" disabled={bannerSaving === "form"}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50">
              {bannerSaving === "form" ? "Saving…" : editingBannerId ? "Update Banner" : "Add Banner"}
            </button>
            {editingBannerId && (
              <button type="button" onClick={() => { setEditingBannerId(null); setBannerForm(emptyBannerForm()); setBannerImageFile(null); setBannerImagePreview(null); setBannerVideoFile(null); setBannerVideoPreview(null); setSelectedSeller(null); setSelectedDeal(null); setSellerSearch(""); setDealSearch(""); setSellerRoleFilter(""); }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm transition-colors">
                Cancel
              </button>
            )}
          </div>
        </form>

        {/* Banner list */}
        {bannersLoading ? (
          <div className="text-center text-gray-500 text-sm py-4"><Spinner /></div>
        ) : banners.length === 0 ? (
          <div className="text-center text-gray-600 text-sm py-4">No banners yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {banners.map((banner) => (
              <div key={banner.id} className="relative rounded-xl overflow-hidden border border-gray-700 bg-gray-800 group">
                {banner.video_url ? (
                  <video src={banner.video_url} className="w-full h-28 object-cover" muted loop autoPlay playsInline />
                ) : banner.image_url ? (
                  <img src={banner.image_url} alt={banner.title ?? banner.category_slug} className="w-full h-28 object-cover" />
                ) : (
                  <div className="w-full h-28 bg-gray-700 flex items-center justify-center text-gray-500 text-xs">No media</div>
                )}
                {banner.video_url && (
                  <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/70 text-white text-xs rounded-md">▶ Video</span>
                )}
                <div className="p-3">
                  {banner.title && <p className="text-white text-xs font-semibold mb-0.5 truncate">{banner.title}</p>}
                  <p className="text-indigo-400 text-xs truncate">/{banner.category_slug}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => void handleBannerToggleActive(banner)} disabled={bannerSaving === banner.id + "active"}
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${banner.active ? "bg-green-900/40 text-green-400" : "bg-gray-700 text-gray-500"}`}>
                      {banner.active ? "Active" : "Off"}
                    </button>
                    {banner.effective_status === "expired" && (
                      <span title={banner.scheduled_deactivate_at ? `Auto-deactivated ${new Date(banner.scheduled_deactivate_at).toLocaleString()}` : undefined}
                        className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-400">Expired</span>
                    )}
                    <span className="text-gray-600 text-xs">p:{banner.priority}</span>
                    <div className="ml-auto flex gap-1">
                      <button onClick={() => setViewBanner(banner)}
                        className="px-2 py-1 bg-indigo-900/40 hover:bg-indigo-900/70 text-indigo-300 rounded-lg text-xs">View</button>
                      <button onClick={() => setDeleteBannerTarget(banner)} disabled={bannerSaving === banner.id}
                        className="px-2 py-1 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-xs">Del</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    {viewBanner && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setViewBanner(null)}>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white truncate">{viewBanner.title || viewBanner.category_slug}</h2>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => { setViewBanner(null); handleBannerEdit(viewBanner); }}
                className="px-3 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-colors"
              >
                Edit
              </button>
              <button onClick={() => setViewBanner(null)} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="rounded-xl overflow-hidden bg-gray-800 flex items-center justify-center">
              {viewBanner.video_url ? (
                <video src={viewBanner.video_url} className="w-full max-h-48 object-cover" controls />
              ) : viewBanner.image_url ? (
                <img src={viewBanner.image_url} alt={viewBanner.title ?? viewBanner.category_slug} className="w-full max-h-48 object-cover" />
              ) : (
                <div className="w-full h-28 flex items-center justify-center text-gray-500 text-xs">No media</div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <span className="text-gray-500">Category Slug</span>
              <span className="text-gray-300">/{viewBanner.category_slug}</span>
              <span className="text-gray-500">Priority</span>
              <span className="text-gray-300">{viewBanner.priority}</span>
              <span className="text-gray-500">Active</span>
              <span className={viewBanner.active ? "text-green-400" : "text-gray-500"}>{viewBanner.active ? "Yes" : "No"}</span>
              <span className="text-gray-500">Auto-deactivates</span>
              <span className={viewBanner.effective_status === "expired" ? "text-amber-400" : "text-gray-300"}>
                {viewBanner.scheduled_deactivate_at
                  ? `${new Date(viewBanner.scheduled_deactivate_at).toLocaleString()}${viewBanner.effective_status === "expired" ? " (expired)" : ""}`
                  : "Never"}
              </span>
            </div>
          </div>
        </div>
      </div>
    )}
    {deleteBannerTarget && (
      <ConfirmModal
        title="Delete Banner"
        description={`Banner for "${deleteBannerTarget.category_slug}" will be permanently removed.`}
        items={["Remove banner image reference", "Banner will no longer appear in chat"]}
        confirmLabel="Delete Banner"
        onConfirm={handleBannerDelete}
        onCancel={() => setDeleteBannerTarget(null)}
      />
    )}
    </>
  );
}
